/**
 * The GUI's copy of the core's configuration (theme, layout, keybinds...).
 *
 * Loaded when the connection opens, then replaced by every `config.changed` event, so saving
 * the file in an editor restyles and re-lays-out the open app at once.
 */
import { createSignal, type Accessor } from "solid-js";
import type { Config } from "@protocol/Config";
import type { ConfigState } from "@protocol/ConfigState";
import { isClientError, type Client } from "../client/client";
import type { MenuSection } from "../shared/menus";

/** The mirrored configuration and the requests about it. */
export interface ConfigMirror {
  /** The state as the core last described it (`null` before the first answer). */
  state: Accessor<ConfigState | null>;
  /** The configuration in use, or `null` before the first answer. */
  config: Accessor<Config | null>;
  /** Checks and saves a new text. Resolves with `null` on success, or the core's explanation. */
  save(text: string): Promise<string | null>;
  /** Asks the core to read the file again. */
  reload(): Promise<void>;
  /** The built-in default text. */
  defaultText(): Promise<string>;
}

/** Starts mirroring the configuration through `client`. */
export function createConfigMirror(client: Client): ConfigMirror {
  const [state, setState] = createSignal<ConfigState | null>(null);

  /** Asks the core for the config (on every (re)connection). */
  const load = async () => {
    try {
      const response = await client.request({ method: "config.get" });
      if (response.type === "config") setState(response.state);
    } catch {
      // Disconnected; the next onOpen loads it.
    }
  };

  client.onOpen(() => void load());
  client.onEvent((event) => {
    if (event.event === "config.changed") setState(event.data.state);
  });

  return {
    state,
    config: () => state()?.config ?? null,
    async save(text) {
      try {
        const response = await client.request({ method: "config.save", params: { text } });
        if (response.type === "config") setState(response.state);
        return null;
      } catch (error) {
        return isClientError(error) ? error.message : String(error);
      }
    },
    async reload() {
      try {
        const response = await client.request({ method: "config.reload" });
        if (response.type === "config") setState(response.state);
      } catch {
        // The error is in the core's log.
      }
    },
    async defaultText() {
      const response = await client.request({ method: "config.default" });
      return response.type === "config_text" ? response.text : "";
    },
  };
}

/** The Tools menu's configuration. */
export function configMenus(): MenuSection[] {
  return [{ menu: "tools", section: "Settings", order: 80, entries: () => [{ label: "Configuration…", command: "window.open", argument: "config" }, { label: "Log", command: "window.open", argument: "log" }] }];
}
